/**
 * Operations: equipment service, repairs, meters and fuel; problems with fences, water, gates,
 * pens and windmills; checklists; tasks and calendar; standard procedures; shows and exhibitions.
 *
 * Distinctions kept honest: a problem reported is not a repair; a repair is not done until it says
 * fixed; a service due date passing never marks work done; a task completed never records work by
 * itself; a reported show result is not an official one; a scratch is not last place.
 */
import { defineEvent, defineFacet, defineModule, f, msg, type Formatters, type Issue, type SubjectRef } from "../../contract";
import {
  CHECK_RESULTS, EQUIPMENT_TYPES, SERVICE_DUE, SERVICE_OVERDUE, FUEL_TYPES, ISSUE_KINDS, METER_SYMBOL, METER_UNITS, PROBLEM_TYPES, REPAIR_OUTCOMES, SERVICE_TYPES, SEVERITY,
  openDays, reduceEquipment, reducePlaceProblems,
  type EquipmentState, type FuelData, type InspectionData, type IssueData, type MeterData, type MeterUnit, type PlaceProblemsState, type RepairData, type ServiceData,
} from "./shared/equipment";
import { REPEAT_RULES } from "./shared/calendar";
import {
  MEASURE_BASIS, RESULT_STATUS, SHOW_MEASURES, SHOW_OUTCOMES, SHOW_TYPES, reduceAnimalShows, resultHeadline,
  type AnimalShowsState, type ShowEntryData, type ShowResultData,
} from "./shared/shows";

export type { AnimalShowsState, EquipmentState, PlaceProblemsState };

// ---------------------------------------------------------------- shared field pieces

const meter = f.number("Meter reading", { min: 0, help: "Hours or miles on the meter", agent: "The number on the hour meter or odometer, only when said" });
const meterUnit = f.enum("Meter reads in", METER_UNITS, { level: "more", help: "Only needed when the equipment's meter isn't set", agent: "Only when the person says hours, miles or kilometers" });

const parts = f.list("Parts used", f.object("Part", {
  part: f.text("Part", { required: true, placeholder: "Oil filter" }),
  part_number: f.text("Part number", { level: "more" }),
  quantity: f.number("How many", { min: 0 }),
  unit: f.text("Unit", { level: "more", placeholder: "each, qt, gal" }),
  stock_item: f.ref("Taken from supplies", "stock_item", { level: "more", help: "Takes it off what's on hand", agent: "Only when they say it came from the ranch's own stock; look it up with find_records" }),
}), { level: "more", itemLabel: "Part" });

const doneBy = f.ref("Done by", "member", { level: "more" });
const shop = f.ref("Shop or mechanic", "contact", { level: "more" });
const receipt = f.ref("Receipt or bill", "fin_doc", { level: "more", help: "Link the cost where your finances keep it" });
const notes = f.longText("Notes", { level: "more" });

const showMeasurements = f.list("Measurements", f.object("Measurement", {
  measure: f.enum("What", SHOW_MEASURES, { required: true }),
  label: f.text("Name it", { level: "more", help: "For other measurements" }),
  value: f.quantity("Value", "any", { required: true }),
  basis: f.enum("How it was taken", MEASURE_BASIS, { level: "more" }),
  official: f.bool("Official measurement", { level: "more" }),
}), { level: "more", itemLabel: "Measurement" });

// ---------------------------------------------------------------- summary helpers

function where(subjects: SubjectRef[], x: Formatters): string {
  return x.list(subjects.filter((s) => s.kind === "asset" || s.kind === "place").map((s) => s.label ?? x.label(s.id)).filter(Boolean));
}

function meterText(x: Formatters, value: number | undefined, unit: MeterUnit | undefined): string | null {
  if (typeof value !== "number") return null;
  return unit ? x.quantity({ value, unit: METER_SYMBOL[unit] }) : x.t("{{value}} on the meter", { value });
}

const prefixed = (place: string, body: string) => (place ? `${place}: ${body}` : body);

function needsAssetOrPlace(subjects: SubjectRef[]): Issue[] {
  return subjects.some((s) => s.kind === "asset" || s.kind === "place") ? [] : [{ path: "subjects.asset", message: "Pick the equipment or the place" }];
}

function partIssues(list: ServiceData["parts"]): Issue[] {
  return (list ?? []).flatMap((p, i) => (p.stock_item && typeof p.quantity !== "number" ? [{ path: `data.parts.${i}.quantity`, message: "Say how many came out of supplies" }] : []));
}

// ---------------------------------------------------------------- equipment and infrastructure events

const service = defineEvent<ServiceData>({
  type: "operations.service",
  label: "Service",
  description: "Maintenance actually done on a vehicle, tractor or other equipment.",
  icon: "wrench",
  tone: "ops",
  subjects: [{ role: "asset", kind: "asset", label: "Equipment", required: true }],
  fields: {
    service_type: f.enum("Service", SERVICE_TYPES, { required: true, agent: "Closest type; use other when none fits and describe it in work" }),
    work: f.text("What was done", { placeholder: "Changed oil and filter, 15W-40" }),
    meter,
    meter_unit: meterUnit,
    parts,
    done_by: doneBy,
    shop,
    receipt,
    next_due_meter: f.number("Next due at (meter)", { level: "more", min: 0, help: "Only if a different point than the schedule", agent: "Only when they state when the next one is due" }),
    next_due_on: f.date("Next due on", { level: "more", help: "Only if a different date than the schedule", agent: "Only when they state a date for the next one" }),
    notes,
  },
  summary: (e, x) => {
    const d = e.data;
    const m = meterText(x, d.meter, d.meter_unit);
    const head = m ? x.t("{{service}} at {{meter}}", { service: x.t(SERVICE_TYPES[d.service_type] ?? d.service_type), meter: m }) : x.t(SERVICE_TYPES[d.service_type] ?? d.service_type);
    return prefixed(where(e.subjects, x), [head, d.work].filter(Boolean).join(" · "));
  },
  review: "always",
  validate: (d) => partIssues(d.parts),
  agent: {
    when: "Maintenance that was actually done on equipment: oil change, grease, filters, tires, belts, battery, inspection. Include the meter reading when said.",
    avoid: "Not for fixing something broken (operations.repair), a meter reading alone (operations.meter), fuel (operations.fuel) or service that is only planned (a task).",
    examples: ["changed the oil on the 6120, 2,340 hours", "greased the baler", "new tires on the F-250 at 45,000 miles", "le cambié el aceite al tractor, 2,340 horas"],
  },
  effects: {
    stock: (d) => (d.parts ?? []).filter((p) => p.stock_item && typeof p.quantity === "number" && p.quantity > 0)
      .map((p) => ({ item_id: p.stock_item!, delta: -p.quantity!, unit: p.unit?.trim() || "each", reason: "use" as const })),
  },
});

const repair = defineEvent<RepairData>({
  type: "operations.repair",
  label: "Repair",
  description: "Something broken was worked on or fixed: equipment, fence, gate, water, windmill or pens.",
  icon: "hammer",
  tone: "ops",
  subjects: [
    { role: "asset", kind: "asset", label: "Equipment" },
    { role: "place", kind: "place", label: "Place" },
  ],
  fields: {
    work: f.text("What was done", { required: true, placeholder: "Replaced the leathers" }),
    outcome: f.enum("Is it fixed?", REPAIR_OUTCOMES, { required: true, agent: "fixed only when they say it's fixed or working; temporary for a patch; not_fixed when it still needs work" }),
    issue_id: f.text("Problem it fixes", { level: "more", agent: "The open problem's id from equipment_status, when this work is on a problem already reported" }),
    problem: f.text("What was wrong", { level: "more", agent: "Only when no reported problem is linked" }),
    out_of_service: f.bool("Can't be used until fixed", { level: "more" }),
    meter,
    meter_unit: meterUnit,
    parts,
    done_by: doneBy,
    shop,
    receipt,
    notes,
  },
  summary: (e, x) => {
    const d = e.data;
    const body = d.outcome === "fixed" ? x.t("Fixed: {{work}}", { work: d.work }) : d.outcome === "temporary" ? x.t("Patched for now: {{work}}", { work: d.work }) : x.t("Worked on, still not fixed: {{work}}", { work: d.work });
    return prefixed(where(e.subjects, x), body);
  },
  review: "always",
  validate: (d, subjects) => [...needsAssetOrPlace(subjects), ...partIssues(d.parts)],
  agent: {
    when: "Something broken was worked on or fixed. When it fixes a problem already reported, set issue_id from equipment_status and include the same equipment and place.",
    avoid: "Not for routine maintenance (operations.service). Not for reporting a new problem that nobody worked on (operations.issue).",
    examples: ["fixed the north windmill leathers", "patched the water line in Pen 2, still needs a new coupler", "replaced the bearing on the baler", "arreglé la puerta del corral"],
  },
  effects: {
    stock: (d) => (d.parts ?? []).filter((p) => p.stock_item && typeof p.quantity === "number" && p.quantity > 0)
      .map((p) => ({ item_id: p.stock_item!, delta: -p.quantity!, unit: p.unit?.trim() || "each", reason: "use" as const })),
  },
});

const issue = defineEvent<IssueData>({
  type: "operations.issue",
  label: "Problem",
  description: "Something found broken or needing repair. It stays open until a repair says it's fixed.",
  icon: "warn",
  tone: "ops",
  subjects: [
    { role: "asset", kind: "asset", label: "Equipment" },
    { role: "place", kind: "place", label: "Place" },
  ],
  fields: {
    problem: f.text("What's wrong", { required: true, placeholder: "Gate off its hinges", agent: "In the speaker's words and language" }),
    kind: f.enum("Kind", ISSUE_KINDS),
    severity: f.enum("How soon", SEVERITY, { agent: "Only when urgency is said or obvious (water out, cattle getting out)" }),
    out_of_service: f.bool("Can't be used until fixed", { level: "more" }),
    location: f.text("Exactly where", { level: "more", placeholder: "North corner by the creek" }),
    parts_needed: f.text("Parts or supplies needed", { level: "more", placeholder: "3/4 inch float valve", help: "Shows on the shopping list until it's fixed" }),
    notes,
  },
  summary: (e, x) => {
    const d = e.data;
    return prefixed(where(e.subjects, x), [d.problem, d.out_of_service ? x.t("Out of service") : null].filter(Boolean).join(" · "));
  },
  validate: (_d, subjects) => needsAssetOrPlace(subjects),
  agent: {
    when: "Someone found something broken or needing repair: a fence down, a gate broken, a leak, a dry tank, a squeaking windmill, a machine that won't start. Link the place and/or the equipment.",
    avoid: "Not for a fix (operations.repair). Create a task only when they ask for someone to do it.",
    examples: ["the gate on the south trap is broken", "water tank in North is leaking", "the 6120 won't start", "se cayó la cerca del potrero sur"],
  },
});

const meterReading = defineEvent<MeterData>({
  type: "operations.meter",
  label: "Meter reading",
  description: "Hours or miles read off a machine's meter.",
  icon: "gauge",
  tone: "ops",
  subjects: [{ role: "asset", kind: "asset", label: "Equipment", required: true }],
  fields: {
    reading: f.number("Reading", { required: true, min: 0 }),
    meter_unit: meterUnit,
    new_meter: f.bool("New or replaced meter", { level: "more", help: "Starts counting again from this reading" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const m = meterText(x, d.reading, d.meter_unit) ?? "";
    return prefixed(where(e.subjects, x), d.new_meter ? x.t("New meter at {{meter}}", { meter: m }) : m);
  },
  agent: {
    when: "Someone read the hour meter or odometer and nothing else was done.",
    avoid: "When they also changed oil, fueled or fixed something, put the reading on that record instead.",
    examples: ["the F-250 is at 45,350 miles", "6120 hour meter reads 2,275"],
  },
});

const fuel = defineEvent<FuelData>({
  type: "operations.fuel",
  label: "Fuel",
  description: "Fuel put in a vehicle or machine.",
  icon: "fuel",
  tone: "ops",
  subjects: [{ role: "asset", kind: "asset", label: "Equipment", required: true }],
  fields: {
    amount: f.quantity("How much", "volume", { required: true, units: ["gal", "l"] }),
    fuel_type: f.enum("Fuel", FUEL_TYPES),
    meter,
    meter_unit: meterUnit,
    receipt,
    notes,
  },
  summary: (e, x) => {
    const d = e.data;
    const amount = x.quantity(d.amount);
    const head = d.fuel_type ? x.t("{{amount}} of {{fuel}}", { amount, fuel: x.t(FUEL_TYPES[d.fuel_type]).toLowerCase() }) : amount;
    return prefixed(where(e.subjects, x), [head, meterText(x, d.meter, d.meter_unit)].filter(Boolean).join(" · "));
  },
  validate: (d) => (d.amount && d.amount.value <= 0 ? [{ path: "data.amount", message: "Enter how much fuel" }] : []),
  agent: {
    when: "Fuel was put in a vehicle or machine. Include the meter reading when said.",
    examples: ["put 30 gallons in the F-250", "filled the tractor with diesel, 42 gallons at 2,290 hours", "le eché 30 galones a la troca"],
  },
});

const inspection = defineEvent<InspectionData>({
  type: "operations.inspection",
  label: "Checklist",
  description: "A walk-through or inspection with a result for each item.",
  icon: "clipboardCheck",
  tone: "ops",
  subjects: [
    { role: "asset", kind: "asset", label: "Equipment" },
    { role: "place", kind: "place", label: "Place" },
  ],
  fields: {
    checklist: f.text("Checklist", { placeholder: "Pre-season baler check" }),
    items: f.list("Items", f.object("Item", {
      item: f.text("Item", { required: true }),
      result: f.enum("Result", CHECK_RESULTS, { required: true }),
      note: f.text("Note"),
    }), { required: true, itemLabel: "Item" }),
    meter,
    meter_unit: meterUnit,
    notes,
  },
  summary: (e, x) => {
    const d = e.data;
    const items = d.items ?? [];
    const ok = items.filter((i) => i.result === "ok").length;
    const flagged = items.filter((i) => i.result === "attention" || i.result === "failed");
    const parts = [x.count(ok, "{{count}} OK", "{{count}} OK")];
    if (flagged.length) parts.push(`${x.count(flagged.length, "{{count}} needs attention", "{{count}} need attention")} (${x.list(flagged.map((i) => i.item))})`);
    return prefixed([where(e.subjects, x), d.checklist].filter(Boolean).join(", "), parts.join(" · "));
  },
  validate: (d, subjects) => [...needsAssetOrPlace(subjects), ...(!d.items?.length ? [{ path: "data.items", message: "Add at least one item" }] : [])],
  agent: {
    when: "A checklist or walk-through with a result per item (OK, needs attention, failed). Items that need attention stay open as problems.",
    examples: ["checked the chute: headgate OK, squeeze OK, tailgate pin worn", "pre-trip on the trailer, lights out on the left side"],
  },
});

// ---------------------------------------------------------------- shows

const showEntry = defineEvent<ShowEntryData>({
  type: "operations.show_entry",
  label: "Show entry",
  description: "An animal entered in a show and class.",
  icon: "award",
  tone: "perf",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", required: true, many: true }],
  fields: {
    show_name: f.text("Show name", { required: true, placeholder: "County Junior Heifer Show", agent: "The show's name; when it's one of the ranch's show jobs, use its title and set job_id" }),
    class_name: f.text("Class", { placeholder: "Class 4" }),
    division: f.text("Division", { level: "more", placeholder: "Junior heifers" }),
    entry_number: f.text("Entry number", { level: "more" }),
    exhibitor: f.text("Exhibitor", { level: "more", help: "Who shows the animal. Not always the owner." }),
    measurements: showMeasurements,
    receipt: f.ref("Entry fee receipt", "fin_doc", { level: "more" }),
    notes,
  },
  summary: (e, x) => [x.t("Entered in {{show}}", { show: e.data.show_name }), e.data.class_name, e.data.division].filter(Boolean).join(" · "),
  agent: {
    when: "An animal was entered in a show or class. When it's one of the ranch's show jobs (see Active jobs), set job_id to it.",
    avoid: "Not for a result (operations.show_result).",
    examples: ["entered 2501 in the junior heifer show, class 4", "metimos a la 2503 en la clase 4"],
  },
});

const showResult = defineEvent<ShowResultData>({
  type: "operations.show_result",
  label: "Show result",
  description: "How an animal did at a show: a placing, an award, shown without placing, scratched or disqualified.",
  icon: "trophy",
  tone: "perf",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", required: true, many: true }],
  fields: {
    show_name: f.text("Show name", { required: true }),
    class_name: f.text("Class"),
    outcome: f.enum("Result", SHOW_OUTCOMES, { required: true, agent: "award for champion, reserve and other named awards; placed for a class placing" }),
    placing: f.int("Place", { min: 1, help: "1 for first" }),
    award: f.text("Award", { placeholder: "Reserve champion" }),
    class_size: f.int("Head in the class", { level: "more", min: 1 }),
    division: f.text("Division", { level: "more" }),
    judge: f.text("Judge", { level: "more" }),
    points: f.number("Points", { level: "more", min: 0 }),
    points_system: f.text("Points system", { level: "more", help: "Points from different systems are never added together" }),
    status: f.enum("Result is", RESULT_STATUS, { level: "more", help: "Leave as told until you have the official results", agent: "official only when they cite official results" }),
    evidence: f.text("Official source", { level: "more", placeholder: "Results sheet, show website" }),
    measurements: showMeasurements,
    notes,
  },
  summary: (e, x) => {
    const d = e.data;
    const parts = [resultHeadline(d, x), d.show_name];
    if (typeof d.points === "number" && d.outcome !== "scratched" && d.outcome !== "disqualified") parts.push(x.count(d.points, "{{count}} point", "{{count}} points"));
    if (d.status === "official") parts.push(x.t("Official"));
    return parts.filter(Boolean).join(" · ");
  },
  validate: (d) => {
    const out: Issue[] = [];
    if (d.outcome === "placed" && typeof d.placing !== "number") out.push({ path: "data.placing", message: "Say what place" });
    if (d.outcome === "award" && !d.award) out.push({ path: "data.award", message: "Name the award" });
    if ((d.outcome === "scratched" || d.outcome === "disqualified") && (d.placing !== undefined || d.points !== undefined)) out.push({ path: "data.placing", message: "A scratched or disqualified entry has no placing or points" });
    if (typeof d.placing === "number" && typeof d.class_size === "number" && d.placing > d.class_size) out.push({ path: "data.placing", message: "The place can't be past the number of head in the class" });
    return out;
  },
  agent: {
    when: "How an animal did at a show. A champion or reserve award is its own result with outcome award, separate from the class placing. A scratch has no placing.",
    examples: ["2501 won her class at the county show", "641 placed first in class three at Houston, Judge Martinez, forty points", "2501 got reserve champion too", "scratched 2504 from class 4"],
  },
});

// ---------------------------------------------------------------- facets

const equipmentFacet = defineFacet<EquipmentState>({
  id: "operations",
  subject: "asset",
  version: 1,
  eventTypes: EQUIPMENT_TYPES,
  reduce: (events) => reduceEquipment(events),
  badges: (s) => (s.open.some((p) => p.out_of_service) ? [{ label: msg("Broken"), tone: "bad", icon: "warn" }] : []),
});

const placeFacet = defineFacet<PlaceProblemsState>({
  id: "operations",
  subject: "place",
  version: 1,
  eventTypes: PROBLEM_TYPES,
  reduce: (events) => reducePlaceProblems(events),
  badges: (s) => (s.open.length ? [{ label: s.open.length === 1 ? msg("1 open problem") : msg("{{count}} open problems", { count: s.open.length }), tone: "warn", icon: "warn" }] : []),
  facts: (s, { today }) => {
    if (!s.open.length) return [];
    const oldest = s.open[0];
    const days = openDays(oldest, today) ?? 0;
    const value = s.open.length > 1
      ? msg("{{count}} open, oldest: {{problem}}", { count: s.open.length, problem: oldest.problem })
      : days === 1 ? msg("{{problem}}, open 1 day", { problem: oldest.problem }) : msg("{{problem}}, open {{count}} days", { problem: oldest.problem, count: days });
    return [{ label: "Open problems", value, tone: "warn", icon: "warn" }];
  },
});

const showsFacet = defineFacet<AnimalShowsState>({
  id: "operations",
  subject: "animal",
  version: 1,
  eventTypes: SHOW_TYPES,
  reduce: (events) => reduceAnimalShows(events),
  facts: (s) => {
    if (s.wins) return [{ label: "Shows", value: s.wins === 1 ? msg("1 class win") : msg("{{count}} class wins", { count: s.wins }), icon: "award" }];
    if (s.results) return [{ label: "Shows", value: s.results === 1 ? msg("1 show result") : msg("{{count}} show results", { count: s.results }), icon: "award" }];
    if (s.last) return [{ label: "Shows", value: msg("Entered in {{show}}", { show: s.last.show_name }), icon: "award" }];
    return [];
  },
});

/** Messages the server sends back on a rejected record; the web translates them like any catalog key. */
const SERVER_MESSAGES = [
  "Pick the equipment or the place", "Say how many came out of supplies", "Enter how much fuel", "Add at least one item", "Say what place", "Name the award",
  "A scratched or disqualified entry has no placing or points", "The place can't be past the number of head in the class",
  "This equipment's meter reads in hours", "This equipment's meter reads in miles", "This equipment's meter reads in kilometers",
  "This equipment's meter reads in a different unit", "Say whether the meter reads hours, miles or kilometers", "A meter reading needs the equipment it was read from",
  "That's lower than an earlier reading on this meter. If the meter was replaced, record it as a new meter first.",
  "That's higher than a later reading on this meter. Check the number and the date.",
  "That problem wasn't found. Pick it again from the open problems.", "Include the same equipment and place as the problem it fixes",
  "The fix can't be before the problem was reported", "That problem is already marked fixed. Report it again if it broke again.",
  "That job isn't a show", "Use the unit supplies counts this part in", "Name the show", "The last day can't be before the first day.",
  "Pick a start and end date.", "Show at most two months at a time.", "That service is already on the schedule",
  "Say how often: by the meter, by months, or both", "Set whether this equipment's meter reads hours or miles first",
  "Name the procedure", "Keep the name under 200 characters", "Add at least one step", "Keep it to 60 steps or fewer",
  "Keep each step under 1,000 characters", "Keep it to 30 words or fewer", "Keep the summary under 2,000 characters",
  // Calendar feed text, written on the server in the subscriber's language.
  "{{ranch}}: my tasks", "{{ranch}}: crew tasks", "(estimated)", "Anyone",
];

// ---------------------------------------------------------------- module

export default defineModule({
  id: "operations",
  label: "Operations",
  description: "Tasks, equipment, maintenance, infrastructure and shows.",
  events: [service, repair, issue, meterReading, fuel, inspection, showEntry, showResult],
  facets: [equipmentFacet, placeFacet, showsFacet],
  jobs: [{
    type: "operations.show",
    label: "Livestock show",
    description: "A show or exhibition: entries by animal and class, then results and awards.",
    icon: "award",
    tone: "perf",
    workspace: "shows",
    subjectKind: "animal",
    fields: {
      location: f.text("Where", { placeholder: "Bell County Expo Center" }),
      ends_on: f.date("Last day", { level: "more" }),
      organizer: f.ref("Organizer", "contact", { level: "more" }),
      rules_version: f.text("Class rules", { help: "Rule book or version the classes follow", placeholder: "2026 premium book" }),
      points_system: f.text("Points system", { level: "more" }),
      classes: f.list("Classes", f.object("Class", {
        name: f.text("Class", { required: true, placeholder: "Class 4" }),
        division: f.text("Division"),
        rules: f.text("Who's eligible", { level: "more", placeholder: "Heifers born Jan 1 to Mar 31, 2025" }),
      }), { level: "more", itemLabel: "Class" }),
      notes,
    },
    agent: { when: "Setting up a show the ranch plans to take cattle to." },
  }],
  workspaces: [
    {
      id: "equipment",
      label: "Maintain equipment",
      description: "Service due, problems to fix, fuel and the work done on every machine.",
      icon: "tractor",
      group: "operations",
      keywords: ["equipment", "maintenance", "service", "repair", "oil change", "tractor", "truck", "fuel", "meter", "hours", "miles", "broken", "fix", "fence", "windmill", "water", "gate", "checklist", "inspection", "parts"],
      keywordsEs: ["equipo", "mantenimiento", "servicio", "reparación", "reparar", "aceite", "tractor", "troca", "camioneta", "combustible", "diésel", "horómetro", "millas", "descompuesto", "cerca", "papalote", "molino", "bebedero", "puerta", "inspección", "refacciones"],
    },
    {
      id: "calendar",
      label: "Tasks and calendar",
      description: "Who's doing what and when, by month, week or day, with repeating chores.",
      icon: "calendar",
      group: "operations",
      keywords: ["calendar", "tasks", "schedule", "due", "assign", "crew", "reminder", "to do", "week", "month", "repeat", "chores", "procedures"],
      keywordsEs: ["calendario", "tareas", "agenda", "pendientes", "asignar", "recordatorio", "semana", "mes", "quehaceres", "procedimientos"],
    },
    {
      id: "shows",
      label: "Shows and exhibitions",
      description: "Entries by animal and class, results, awards and season records.",
      icon: "award",
      group: "sell",
      keywords: ["show", "shows", "exhibition", "fair", "class", "entry", "placing", "champion", "judge", "points", "ribbon"],
      keywordsEs: ["exposición", "feria", "concurso", "clase", "inscripción", "lugar", "campeona", "campeón", "juez", "puntos", "listón"],
      presets: ["seedstock"],
    },
  ],
  ranchSections: [{
    id: "procedures",
    label: "Procedures",
    description: "How this ranch does a job, step by step. Shown beside matching tasks.",
    icon: "book",
    group: "equipment",
    keywords: ["procedures", "SOP", "how to", "steps", "instructions", "checklist"],
    keywordsEs: ["procedimientos", "pasos", "instrucciones", "cómo hacer", "lista"],
  }],
  attributes: {
    asset: {
      schedule: f.list("Service schedule", f.object("Interval", {
        service_type: f.enum("Service", SERVICE_TYPES, { required: true }),
        every_meter: f.number("Every (meter)", { min: 1, help: "Hours or miles, in the equipment's meter unit" }),
        every_months: f.int("Every (months)", { min: 1, max: 120 }),
        note: f.text("Note", { placeholder: "15W-40, 4 gal" }),
      }), { level: "more", itemLabel: "Interval" }),
    },
  },
  messages: [...Object.values(REPEAT_RULES), ...new Set([...Object.values(SERVICE_OVERDUE), ...Object.values(SERVICE_DUE)]), ...SERVER_MESSAGES],
});
